import {Role} from '@cdo/apps/aiComponentLibrary/chatMessage/types';
import {AiInteractionStatus as Status} from '@cdo/generated-scripts/sharedConstants';

import {ChatEvent, isChatMessage} from '../types';

/**
 * A row holds either the raw JSON the model returned or prose, with nothing to
 * mark which: messages saved before the display transform moved to render time
 * had formatForDisplay applied first. Hence the parse attempt.
 *
 * bin/cron/delete_old_ai_chat_data drops aichat_events rows after 90 days, so
 * the prose case can go 90 days after this ships.
 */
export function applySchemaDisplayTransform(
  events: ChatEvent[],
  formatForDisplay?: (response: unknown) => string
): ChatEvent[] {
  if (!formatForDisplay) {
    return events;
  }

  let changed = false;
  const transformed = events.map(event => {
    const next = transformEvent(event, formatForDisplay);
    if (next !== event) {
      changed = true;
    }
    return next;
  });

  // Preserve referential identity when nothing changed, so memoized consumers
  // do not re-render on every call.
  return changed ? transformed : events;
}

function transformEvent(
  event: ChatEvent,
  formatForDisplay: (response: unknown) => string
): ChatEvent {
  if (!isChatMessage(event)) {
    return event;
  }
  // Only successful model output is schema-shaped: user messages are the user's
  // own words, and errored assistant messages carry a placeholder, not JSON.
  if (event.role !== Role.ASSISTANT || event.status !== Status.OK) {
    return event;
  }

  const parsed = parseJsonObject(event.chatMessageText);
  if (parsed === undefined) {
    return event;
  }

  try {
    return {...event, chatMessageText: formatForDisplay(parsed)};
  } catch {
    // A callback that cannot handle this payload must not blank the message or
    // crash the transcript.
    return event;
  }
}

/**
 * Returns the parsed value only for JSON objects and arrays. Bare JSON scalars
 * are excluded deliberately: a stored message of "42" or "true" is text that
 * happens to be valid JSON, not a schema response.
 */
export function parseJsonObject(text: string): unknown | undefined {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return undefined;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}
